/**
 * Who the signed-in user is, as the app shows them — `GET /api/v1/me/`.
 *
 * Separate from `accountService` next door, which asks whether the session's
 * account is provisioned and is answered once at sign-in. This asks for the
 * name and the picture the app draws, and is asked again whenever those change:
 * the record behind it is the practitioner profile the Settings form writes, so
 * a saved photo shows up in the rail on the next read rather than on the next
 * sign-in.
 */

import { api, API_ENDPOINTS } from "../../../lib/api";
// The module rather than the feature's index: `mediaUrl` is about the API's
// origin, not about the brand screen, and reaching for it through `../../brand`
// would pull that whole screen in behind it. Same import `settingsService`
// makes, for the same file's URL.
import { mediaUrl } from "../../brand/services/fileService";
import type { CurrentUser, GetToken, MeResponse } from "../types/auth.types";

/**
 * The response as the app holds it.
 *
 * Exported for the suite, and because it is the whole of the mapping: every
 * absent field becomes `""` rather than `null`, so a call site can ask whether
 * there is a name without asking which kind of nothing it has.
 */
export const toCurrentUser = (response: MeResponse): CurrentUser => ({
  clerkUserId: response.clerk_user_id ?? "",
  email: response.email ?? "",
  fullName: response.full_name?.trim() ?? "",
  firstName: response.first_name?.trim() ?? "",
  lastName: response.last_name?.trim() ?? "",
  // Absolute: the media lives on the API's origin, not the app's, and a path
  // taken as it comes would resolve against whatever screen drew it.
  photoUrl: response.profile_photo?.url
    ? mediaUrl(response.profile_photo.url)
    : "",
});

/**
 * The signed-in user, as our backend holds them.
 *
 * Only works once the session is active — `getToken()` returns null before
 * `setActive()` has resolved, and the endpoint is 401 without a bearer token.
 */
export async function fetchCurrentUser(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<CurrentUser> {
  const token = await getToken();
  if (!token) {
    // Not an ApiError: the server was never asked. Calling this without an
    // active session is a bug at the call site, not something to show a user.
    throw new Error("No active Clerk session - cannot read the current user.");
  }

  const response = await api.get<MeResponse>(API_ENDPOINTS.auth.me, {
    token,
    signal,
  });

  return toCurrentUser(response);
}
